import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { useWarehouseInventoryActions } from './useWarehouseInventoryActions';
import { useWarehouseInventoryDnd } from './useWarehouseInventoryDnd';
import { useWarehouseRename } from './useWarehouseRename';

import type { Warehouse } from '@entities/equipment';

interface UseWarehouseColumnParams {
    warehouse: Warehouse;
    warehouses: Warehouse[];
    inventoryItems?: Warehouse['items'];
    activeInventoryGuid?: string | null;
    hasFullAccess: boolean;
}

export const useWarehouseColumn = ({
    warehouse,
    warehouses,
    inventoryItems,
    activeInventoryGuid,
    hasFullAccess,
}: UseWarehouseColumnParams) => {
    const { t } = useTranslation();
    const availableWarehouses = useMemo(
        () =>
            warehouses
                .filter((warehouseItem) => warehouseItem.id !== warehouse.id)
                .map((warehouseItem) => ({
                    id: warehouseItem.id,
                    title: warehouseItem.title,
                })),
        [warehouse.id, warehouses]
    );
    const inventoryDnd = useWarehouseInventoryDnd({
        warehouseId: warehouse.id,
        warehouseItems: warehouse.items,
        inventoryItems,
        activeInventoryGuid,
    });
    const rename = useWarehouseRename({
        warehouse,
        hasFullAccess,
    });
    const inventoryActions = useWarehouseInventoryActions({
        warehouse,
        warehouses,
    });

    return {
        t,
        ...inventoryDnd,
        availableWarehouses,
        canMoveWarehouseItems: warehouse.items.length > 0 && warehouses.length > 1,
        ...rename,
        ...inventoryActions,
    };
};
